import * as echarts from 'echarts';
import { format, isSameDay, isSameHour, parseISO } from 'date-fns';
import { ViewContainerRef } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { TopLevelFormatterParams } from 'echarts/types/dist/shared';
import { PriceCurveEntry } from '../../../../core/clients/omni-core/models';
import { render } from '../../../../core/helper/render';
import { AnalysisPriceGraphTooltip } from '../../components/analysis-price-graph-tooltip/analysis-price-graph-tooltip';
import { de } from 'date-fns/locale';
import { PriceFilter } from '../../stores/price.store';
import { PriceConversionService } from '../../services/price-conversion.service';
import { ChartConfigBuilder } from '../chart-config-builder';
import { getXLabel, getInterval } from '../util';

const isHistorical = (entry: PriceCurveEntry): boolean =>
  entry.lowerEstimate === entry.price && entry.upperEstimate === entry.price;

export const getTimeBucketKey = (date: Date, granularity: PriceFilter['granularity']): string => {
  if (granularity === 'WEEK') {
    return format(date, 'i HH:mm');
  }
  return format(date, 'MM-dd HH:mm');
};

interface PriceLineChartDependencies {
  vcr: ViewContainerRef;
  translocoService: TranslocoService;
  priceConversionService?: PriceConversionService;
}

interface PriceLineChartOptions {
  granularity: PriceFilter['granularity'];
  showEurMwh: boolean;
  comparisonData?: PriceCurveEntry[] | null;
  comparisonYear?: number | string | null;
}

export class PriceLineChartConfig implements ChartConfigBuilder {
  private readonly xAxisData: string[];
  private readonly priceConversionService: PriceConversionService;

  private readonly marketPriceData: (number | null)[];
  private readonly prognosisPriceData: (number | null)[];
  private readonly confidenceMinData: (number | null)[];
  private readonly confidenceBandData: (number | null)[];
  private readonly comparisonMap: Map<string, PriceCurveEntry> | null;
  private readonly comparisonPriceData: (number | null)[] | null;

  private readonly todayIndex: number;
  private readonly showTodayLine: boolean;
  private readonly prognosisStartIndex: number;
  private readonly showPrognosisLine: boolean;

  private readonly marketPriceName: string;
  private readonly prognosisPriceName: string;
  private readonly comparisonYear: number | string | undefined;
  private readonly comparisonPriceName: string;

  constructor(
    private readonly dependencies: PriceLineChartDependencies,
    private readonly data: PriceCurveEntry[],
    private readonly options: PriceLineChartOptions,
  ) {
    this.xAxisData = data.map((entry) =>
      format(parseISO(entry.validFrom), 'yyyy-MM-dd HH:mm', { locale: de }),
    );

    this.priceConversionService =
      dependencies.priceConversionService ?? new PriceConversionService();

    const showEurMwh = options.showEurMwh;

    this.marketPriceData = data.map((entry) =>
      isHistorical(entry)
        ? this.priceConversionService.convertPrice(entry.price, showEurMwh)
        : null,
    );
    this.prognosisPriceData = data.map((entry) =>
      isHistorical(entry)
        ? null
        : this.priceConversionService.convertPrice(entry.price, showEurMwh),
    );
    this.confidenceMinData = data.map((entry) =>
      isHistorical(entry)
        ? null
        : this.priceConversionService.convertPrice(entry.lowerEstimate, showEurMwh),
    );
    this.confidenceBandData = data.map((entry) =>
      isHistorical(entry)
        ? null
        : this.priceConversionService.convertPrice(
            entry.upperEstimate - entry.lowerEstimate,
            showEurMwh,
          ),
    );

    this.comparisonMap =
      options.comparisonData && options.comparisonData.length > 0
        ? new Map(
            options.comparisonData.map((entry) => [
              getTimeBucketKey(parseISO(entry.validFrom), options.granularity),
              entry,
            ]),
          )
        : null;

    this.comparisonPriceData = this.comparisonMap
      ? data.map((entry) => {
          const key = getTimeBucketKey(parseISO(entry.validFrom), options.granularity);
          const comparisonEntry = this.comparisonMap!.get(key);
          return comparisonEntry != null
            ? this.priceConversionService.convertPrice(comparisonEntry.price, showEurMwh)
            : null;
        })
      : null;

    const now = new Date();
    this.todayIndex = data.findIndex((entry) => {
      const from = parseISO(entry.validFrom);
      if (options.granularity === 'WEEK' || options.granularity === 'DAY') {
        if (entry.validTo) {
          const to = parseISO(entry.validTo);
          return now >= from && now < to;
        }
        return isSameHour(from, now);
      }
      return isSameDay(from, now);
    });
    this.showTodayLine = this.todayIndex > 0 && this.todayIndex < data.length - 1;

    this.prognosisStartIndex = data.findIndex((entry) => !isHistorical(entry));
    this.showPrognosisLine =
      this.prognosisStartIndex > 0 && this.prognosisStartIndex < data.length - 1;

    this.marketPriceName = dependencies.translocoService.translate(
      'cockpit.analysis.hpfc.legend.marketPrice',
    );
    this.prognosisPriceName = dependencies.translocoService.translate(
      'cockpit.analysis.hpfc.legend.prognosis',
    );

    this.comparisonYear =
      options.comparisonYear ||
      (options.comparisonData && options.comparisonData.length > 0
        ? parseISO(options.comparisonData[0].validFrom).getFullYear()
        : undefined);
    this.comparisonPriceName = dependencies.translocoService
      .translate('cockpit.analysis.hpfc.legend.comparison', {
        year: this.comparisonYear ?? '',
      })
      .trim();
  }

  private buildGrid(): echarts.EChartsOption['grid'] {
    return {
      left: 0,
      right: 0,
      top: 32,
      bottom: 0,
    };
  }

  private buildLegend(): echarts.EChartsOption['legend'] {
    return {
      show: true,
      top: 0,
      left: 'right',
      itemStyle: {
        opacity: 0,
      },
    };
  }

  private buildXAxis(): echarts.EChartsOption['xAxis'] {
    return {
      type: 'category',
      boundaryGap: false,
      data: this.xAxisData,
      axisTick: {
        show: false,
      },
      axisLabel: {
        hideOverlap: true,
        interval: (index: number, value: string) => getInterval(value, this.options.granularity),
        formatter: (value: string) => getXLabel(value, this.options.granularity),
      },
    };
  }

  private buildYAxis(): echarts.EChartsOption['yAxis'] {
    return [
      {
        type: 'value',
        name: this.dependencies.translocoService.translate(
          this.options.showEurMwh
            ? 'cockpit.analysis.hpfc.detail.yAxisLabelEurMwh'
            : 'cockpit.analysis.hpfc.detail.yAxisLabel',
        ),
      },
    ];
  }

  private buildTooltip(): echarts.EChartsOption['tooltip'] {
    return {
      trigger: 'axis',
      formatter: (params: TopLevelFormatterParams) => {
        const items = Array.isArray(params) ? params : [params];
        const dataIndex = items[0].dataIndex as number;
        const entry = this.data[dataIndex];
        if (!entry) return '';

        const prognosis = !isHistorical(entry);
        const comparisonEntry = this.comparisonMap?.get(
          getTimeBucketKey(parseISO(entry.validFrom), this.options.granularity),
        );

        return render(this.dependencies.vcr, AnalysisPriceGraphTooltip, {
          date: parseISO(entry.validFrom),
          price: entry.price,
          isPrognosis: prognosis,
          lowerEstimate: prognosis ? entry.lowerEstimate : undefined,
          upperEstimate: prognosis ? entry.upperEstimate : undefined,
          granularity: this.options.granularity,
          showEurMwh: this.options.showEurMwh,
          comparisonPrice: comparisonEntry?.price,
          comparisonYear: this.comparisonYear,
        });
      },
    };
  }

  private buildMarketPriceSeries(): echarts.SeriesOption {
    return {
      id: 'market-price',
      name: this.marketPriceName,
      type: 'line',
      step: 'end',
      symbol: 'none',
      yAxisIndex: 0,
      z: 3,
      data: this.marketPriceData,
      itemStyle: {
        opacity: 0,
      },
      lineStyle: {
        color: 'rgb(0, 150, 137)',
        width: 2,
        type: 'solid',
      },
      emphasis: {
        disabled: true,
      },
      markLine: {
        silent: true,
        symbol: ['none', 'none'],
        label: {
          show: true,
          formatter: this.dependencies.translocoService.translate('cockpit.analysis.hpfc.today'),
          color: 'rgb(153, 161, 175)',
          position: 'insideEndTop',
        },
        lineStyle: {
          color: 'rgb(153, 161, 175)',
          width: 1,
          type: 'solid',
        },
        data: this.showTodayLine
          ? [
              {
                xAxis: this.todayIndex,
              },
            ]
          : [],
      },
      markArea: {
        silent: true,
        itemStyle: {
          color: 'transparent',
        },
        data: this.showTodayLine
          ? [
              [
                {
                  xAxis: this.todayIndex,
                },
                {
                  xAxis: this.xAxisData.length - 1,
                },
              ],
            ]
          : [],
      },
    };
  }

  private buildPrognosisSeries(): echarts.SeriesOption {
    return {
      id: 'price-prognosis',
      name: this.prognosisPriceName,
      type: 'line',
      step: 'end',
      symbol: 'none',
      yAxisIndex: 0,
      z: 4,
      data: this.prognosisPriceData,
      itemStyle: {
        opacity: 0,
      },
      lineStyle: {
        color: 'rgb(0, 150, 137)',
        width: 1.25,
        type: [2, 3],
      },
      emphasis: {
        disabled: true,
      },
      markLine: {
        silent: true,
        symbol: ['none', 'none'],
        label: {
          show: false,
        },
        lineStyle: {
          color: 'rgb(209, 213, 220)',
          width: 1,
          type: [3, 3],
        },
        data: this.showPrognosisLine
          ? [
              {
                xAxis: this.prognosisStartIndex,
              },
            ]
          : [],
      },
      markArea: {
        silent: true,
        itemStyle: {
          color: 'transparent',
        },
        label: {
          show: true,
          formatter: this.prognosisPriceName,
          color: 'rgb(212, 212, 216)',
          position: 'insideTop',
        },
        data: this.showPrognosisLine
          ? [
              [
                {
                  name: this.prognosisPriceName,
                  xAxis: this.prognosisStartIndex,
                },
                {
                  xAxis: this.xAxisData.length - 1,
                },
              ],
            ]
          : [],
      },
    };
  }

  private buildConfidenceBandSeries(): echarts.SeriesOption[] {
    return [
      {
        id: 'prognosis-confidence-minimum',
        name: this.prognosisPriceName,
        type: 'line',
        step: 'end',
        symbol: 'none',
        yAxisIndex: 0,
        z: 1,
        stack: 'prognosis-confidence',
        stackStrategy: 'all',
        data: this.confidenceMinData,
        lineStyle: {
          opacity: 0,
        },
        areaStyle: {
          opacity: 0,
        },
        emphasis: {
          disabled: true,
        },
        silent: true,
        tooltip: {
          show: false,
        },
      },
      {
        id: 'prognosis-confidence-band',
        name: this.prognosisPriceName,
        type: 'line',
        step: 'end',
        symbol: 'none',
        yAxisIndex: 0,
        z: 1,
        stack: 'prognosis-confidence',
        stackStrategy: 'all',
        data: this.confidenceBandData,
        lineStyle: {
          opacity: 0,
        },
        areaStyle: {
          color: 'rgba(0, 150, 137, 0.18)',
        },
        emphasis: {
          disabled: true,
        },
        silent: true,
      },
    ];
  }

  private buildComparisonSeries(): echarts.SeriesOption[] {
    if (!this.comparisonPriceData) {
      return [];
    }

    return [
      {
        id: 'comparison-price',
        name: this.comparisonPriceName,
        type: 'line' as const,
        step: 'end' as const,
        symbol: 'none',
        yAxisIndex: 0,
        z: 2,
        connectNulls: true,
        data: this.comparisonPriceData,
        itemStyle: {
          opacity: 0,
        },
        lineStyle: {
          color: 'rgba(21, 93, 252, 0.35)',
          width: 1.5,
          type: 'solid' as const,
        },
        emphasis: {
          disabled: true,
        },
      },
    ];
  }

  private buildMedia(): echarts.EChartsOption['media'] {
    return [
      {
        query: {
          maxWidth: 700,
        },
        option: {
          series: [
            {
              id: 'price-prognosis',
              lineStyle: {
                width: 0.85,
              },
            },
            {
              id: 'market-price',
              lineStyle: {
                width: 1.25,
              },
            },
            ...(this.comparisonPriceData
              ? [
                  {
                    id: 'comparison-price',
                    lineStyle: {
                      width: 1,
                    },
                  },
                ]
              : []),
          ],
        },
      },
    ];
  }

  toEchartsOption(): echarts.EChartsOption {
    return {
      color: ['rgb(0, 150, 137)', 'rgb(0, 150, 137)'],
      grid: this.buildGrid(),
      legend: this.buildLegend(),
      xAxis: this.buildXAxis(),
      yAxis: this.buildYAxis(),
      tooltip: this.buildTooltip(),
      series: [
        this.buildMarketPriceSeries(),
        this.buildPrognosisSeries(),
        ...this.buildConfidenceBandSeries(),
        ...this.buildComparisonSeries(),
      ],
      media: this.buildMedia(),
    };
  }
}

export const getLineConfig = (
  dependencies: PriceLineChartDependencies,
  data: PriceCurveEntry[],
  options: PriceLineChartOptions,
): echarts.EChartsOption => {
  return new PriceLineChartConfig(dependencies, data, options).toEchartsOption();
};
